import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Package, TrendingUp, DollarSign, Info, Layers } from "lucide-react";
import { Product } from "../../types/product";
import { getAdminProducts } from "../../services/productsApi";
import PageHeader from "../../components/ui/PageHeader";
import GlobalLoader from "../../components/ui/GlobalLoader";
import { useAuth } from "../../auth/AuthContext";
import { cn } from "../../lib/utils";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

export default function ProductsSummaryPage() {
  const { user } = useAuth();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "";
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<Product[]>([]);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const data = await getAdminProducts();
      setProducts(data || []);
    } catch (error) {
      console.error("Failed to fetch products", error);
    } finally {
      setLoading(false);
    }
  };

  const stats = useMemo(() => {
    const totalValue = products
      .reduce((sum, p) => sum + (p.price || 0), 0)
      .toFixed(2);
    const avgPrice =
      products.length > 0
        ? (
          products.reduce((sum, p) => sum + (p.price || 0), 0) /
          products.length
        ).toFixed(2)
        : "0.00";
    const maxPrice =
      products.length > 0 ? Math.max(...products.map((p) => p.price || 0)) : 0;

    return { totalValue, avgPrice, maxPrice };
  }, [products]);

  const topProducts = useMemo(() => {
    return [...products]
      .sort((a, b) => (b.price || 0) - (a.price || 0))
      .slice(0, 5);
  }, [products]);

  const priceDistributionData = useMemo(() => {
    const ranges = [
      { name: `${currencySymbol}0-20`, min: 0, max: 20, count: 0 },
      { name: `${currencySymbol}21-50`, min: 21, max: 50, count: 0 },
      { name: `${currencySymbol}51-100`, min: 51, max: 100, count: 0 },
      { name: `${currencySymbol}100+`, min: 101, max: Infinity, count: 0 },
    ];

    products.forEach((p) => {
      const price = p.price || 0;
      const range = ranges.find((r) => price >= r.min && price <= r.max);
      if (range) range.count++;
    });

    return ranges;
  }, [products, currencySymbol]);

  const topProductsChartData = useMemo(() => {
    return topProducts.map((p) => ({
      name: p.name.length > 15 ? p.name.substring(0, 15) + "..." : p.name,
      fullName: p.name,
      price: p.price || 0,
    }));
  }, [topProducts]);

  if (loading) {
    return (
      <div className="flex h-[80vh] items-center justify-center">
        <GlobalLoader description="Loading products summary..." />
      </div>
    );
  }

  return (
    <div className="w-full">
      <PageHeader
        title="Products Summary"
        description="Inventory analytics and product performance"
        icon={Package}
        variant="default"
        className="mb-0"
      />

      <div className="space-y-6 pt-4">
        {/* Metric Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            label="Total Products"
            value={products.length}
            icon={Package}
            trend="+3 new items"
            trendUp={true}
          />
          <MetricCard
            label="Portfolio Value"
            value={`${currencySymbol}${stats.totalValue}`}
            icon={TrendingUp}
          />
          <MetricCard
            label="Average Price"
            value={`${currencySymbol}${stats.avgPrice}`}
            icon={DollarSign}
          />
          <MetricCard
            label="Highest Price Item"
            value={`${currencySymbol}${stats.maxPrice}`}
            icon={Layers}
          />
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Price Distribution Bar Chart */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h3 className="text-lg font-semibold text-[#402B69] mb-6">
              Price Ranges
            </h3>
            <div className="h-[250px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={priceDistributionData}
                  margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#F3F4F6"
                  />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#6B7280", fontSize: 12 }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#6B7280", fontSize: 12 }}
                  />
                  <RechartsTooltip
                    cursor={{ fill: "#F9FAFB" }}
                    contentStyle={{
                      borderRadius: "8px",
                      border: "none",
                      boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                    }}
                  />
                  <Bar
                    dataKey="count"
                    fill="#885ABB"
                    radius={[4, 4, 0, 0]}
                    barSize={40}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top Products Chart */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h3 className="text-lg font-semibold text-[#402B69] mb-6">
              High Value Items
            </h3>
            <div className="h-[250px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={topProductsChartData}
                  layout="vertical"
                  margin={{ top: 0, right: 30, left: 20, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    horizontal={true}
                    vertical={false}
                    stroke="#F3F4F6"
                  />
                  <XAxis type="number" hide />
                  <YAxis
                    dataKey="name"
                    type="category"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#4B5563", fontSize: 12 }}
                    width={100}
                  />
                  <RechartsTooltip
                    cursor={{ fill: "#F9FAFB" }}
                    contentStyle={{
                      borderRadius: "8px",
                      border: "none",
                      boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                    }}
                    formatter={(value: any) => [`${currencySymbol}${value ?? 0}`, "Price"]}
                  />
                  <Bar
                    dataKey="price"
                    fill="#885ABB"
                    radius={[0, 4, 4, 0]}
                    barSize={20}
                    background={{ fill: "#F9FAFB" }}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Product List Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-[#402B69]">
              Highest Value Products
            </h3>
            <Link
              to="/manage/products"
              className="text-sm text-[#885ABB] font-medium hover:text-[#6f47a2] transition-colors"
            >
              View All
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-[#885ABB] text-white font-medium">
                <tr>
                  <th className="px-6 py-3 font-semibold">Product</th>
                  <th className="px-6 py-3 font-semibold">Net Price</th>
                  <th className="px-6 py-3 font-semibold">Tax (GST/VAT)</th>
                  <th className="px-6 py-3 font-semibold">Gross Price</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {topProducts.map((product) => {
                  const totalTaxPercent = product.taxes?.reduce((sum, tax) => sum + tax.percent, 0) || 0;
                  const originalPriceIncTax = product.price * (1 + totalTaxPercent / 100);
                  const discountedPriceIncTax = (product.discountedPrice && product.discountedPrice < product.price)
                    ? product.discountedPrice * (1 + totalTaxPercent / 100)
                    : null;

                  return (
                    <tr
                      key={product.id}
                      className="group hover:bg-gray-50/50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 bg-purple-50 rounded-lg flex items-center justify-center shrink-0 overflow-hidden border border-purple-100">
                            {product.image ? (
                              <img
                                src={product.image}
                                alt=""
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <Package className="w-5 h-5 text-[#885ABB]" />
                            )}
                          </div>
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-900 group-hover:text-[#885ABB] transition-colors">
                              {product.name}
                            </span>
                            {product.sku && (
                              <span className="text-[10px] text-gray-400">SKU: {product.sku}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      {/* Net Price (Excl. Tax) */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        <div className="flex items-center gap-2">
                          <span className={cn(product.discountedPrice && product.discountedPrice < product.price && "line-through text-gray-400 text-[10px]")}>
                            {currencySymbol}
                            {Number(product.price).toFixed(2)}
                          </span>
                          {product.discountedPrice &&
                            product.discountedPrice < product.price && (
                              <span className="text-[#885ABB] font-bold text-xs">
                                {currencySymbol}
                                {Number(product.discountedPrice).toFixed(2)}
                              </span>
                            )}
                        </div>
                      </td>
                      {/* Tax Amount */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex flex-col items-start">
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium">
                              {currencySymbol}
                              {(
                                (product.discountedPrice &&
                                  product.discountedPrice < product.price
                                  ? product.discountedPrice
                                  : product.price) *
                                (totalTaxPercent / 100)
                              ).toFixed(2)}
                            </span>
                          </div>
                          {product.taxes && product.taxes.length > 0 && (
                            <div className="flex flex-wrap gap-x-2 text-[10px] text-gray-400">
                              {product.taxes.map((t, idx) => {
                                const basePrice = (product.discountedPrice && product.discountedPrice < product.price) ? product.discountedPrice : product.price;
                                const taxAmount = basePrice * (t.percent / 100);
                                return (
                                  <span key={idx} className="whitespace-nowrap">
                                    {t.name}: {currencySymbol}{taxAmount.toFixed(2)} ({t.percent}%)
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </td>
                      {/* Gross Price (Inc. Tax) */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-[#885ABB]">
                        <div className="flex items-center gap-2">
                          <span className={cn(discountedPriceIncTax !== null && "line-through text-gray-400 text-[10px]")}>
                            {currencySymbol}{originalPriceIncTax.toFixed(2)}
                          </span>
                          {discountedPriceIncTax !== null && (
                            <span className="font-bold">
                              {currencySymbol}{discountedPriceIncTax.toFixed(2)}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({
  label,
  value,
  icon: Icon,
  subValue,
  trend,
  trendUp,
}: any) {
  return (
    <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-between h-full">
      <div className="flex items-start justify-between mb-4">
        <div className="p-2.5 bg-purple-50 rounded-lg">
          <Icon className="w-5 h-5 text-[#885ABB]" />
        </div>
        {trend && (
          <span
            className={`text-xs font-medium px-2 py-1 rounded-full ${trendUp ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600"
              }`}
          >
            {trend}
          </span>
        )}
      </div>
      <div>
        <h3 className="text-sm font-medium text-gray-500 mb-1">{label}</h3>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold text-gray-900">{value}</span>
          {subValue && (
            <span className="text-xs text-gray-400 truncate max-w-[120px]">
              {subValue}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
